/**
 * DevicesPage - Pagina de gestionare a aparatelor medicale
 * Design compact consistent cu restul aplicației
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  CircularProgress,
  Grid,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import {
  DeviceType,
  DeviceListItem,
  DeviceCreate,
  DeviceUpdate,
  DevicesFilters,
  DEVICE_STATUS_CONFIG,
} from '../types/devices';
import {
  getDeviceTypes,
  getDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  getDevice,
} from '../services/devicesApi';
import apiClient from '../services/api';

// Compact styles - consistent cu restul aplicației
const compactInputSx = {
  '& .MuiInputBase-root': { fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
};

const tableHeaderSx = {
  bgcolor: 'grey.900',
  '& th': {
    color: 'common.white',
    fontSize: '0.75rem',
    fontWeight: 600,
    py: 0.5,
    px: 1
  }
};

const tableCellSx = {
  fontSize: '0.8rem',
  py: 0.5,
  px: 1
};

const dialogTitleSx = {
  py: 1.5,
  px: 2,
  fontSize: '1rem',
  fontWeight: 600,
  borderBottom: '1px solid',
  borderColor: 'divider'
};

const dialogActionsSx = {
  px: 2,
  py: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider'
};

const getDeviceIcon = () => {
  return <SettingsIcon sx={{ fontSize: '0.9rem' }} />;
};

interface Institution {
  id: string;
  name: string;
}

const DevicesPage: React.FC = () => {
  const { user } = useAuth();
  
  // State
  const [devices, setDevices] = useState<DeviceListItem[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState<DevicesFilters>({
    skip: 0,
    limit: 25,
  });
  const [searchInput, setSearchInput] = useState('');
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceListItem | null>(null);
  const [formData, setFormData] = useState<DeviceCreate>({
    tenant_id: '',
    device_type_id: 0,
    code: '',
    name: '',
    room: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<DeviceListItem | null>(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Load device types
  useEffect(() => {
    const loadDeviceTypes = async () => {
      try {
        const types = await getDeviceTypes();
        setDeviceTypes(types);
      } catch (error) {
        console.error('Error loading device types:', error);
      }
    };
    loadDeviceTypes();
  }, []);

  // Load institutions
  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const response = await apiClient.get('/tenants?limit=100');
        setInstitutions(response.data?.items || response.data || []);
      } catch (error) {
        console.error('Error loading institutions:', error);
      }
    };
    loadInstitutions();
  }, []);

  // Load devices
  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDevices(filters);
      setDevices(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading devices:', error);
      setSnackbar({ open: true, message: 'Eroare la încărcarea aparatelor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Filter handlers
  const handleSearch = useCallback(() => {
    setFilters(prev => ({ ...prev, search: searchInput, skip: 0 }));
  }, [searchInput]);

  const handleFilterChange = (key: keyof DevicesFilters, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value, skip: 0 }));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, skip: newPage * (prev.limit || 25) }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, limit: parseInt(event.target.value, 10), skip: 0 }));
  };

  // Dialog handlers
  const handleOpenCreate = () => {
    setEditingDevice(null);
    setFormData({
      tenant_id: user?.tenant_id || '',
      device_type_id: deviceTypes.length > 0 ? deviceTypes[0].id : 0,
      code: '',
      name: '',
      room: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = async (device: DeviceListItem) => {
    setEditingDevice(device);
    setFormLoading(true);
    setDialogOpen(true);
    
    try {
      const fullDevice = await getDevice(device.id);
      setFormData({
        tenant_id: fullDevice.tenant_id,
        device_type_id: fullDevice.device_type_id,
        code: fullDevice.code,
        name: fullDevice.name,
        brand: fullDevice.brand,
        model: fullDevice.model,
        serial_number: fullDevice.serial_number,
        room: fullDevice.room,
        dicom_ae_title: fullDevice.dicom_ae_title,
        notes: fullDevice.notes,
      });
    } catch (error) {
      console.error('Error loading device details:', error);
      setSnackbar({ open: true, message: 'Eroare la încărcarea detaliilor', severity: 'error' });
      setDialogOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name || !formData.device_type_id || !formData.tenant_id) {
      setSnackbar({ open: true, message: 'Completați toate câmpurile obligatorii', severity: 'error' });
      return;
    }

    setFormLoading(true);
    try {
      if (editingDevice) {
        const updateData: DeviceUpdate = { ...formData };
        await updateDevice(editingDevice.id, updateData);
        setSnackbar({ open: true, message: 'Aparatul a fost actualizat', severity: 'success' });
      } else {
        await createDevice(formData);
        setSnackbar({ open: true, message: 'Aparatul a fost creat', severity: 'success' });
      }
      setDialogOpen(false);
      loadDevices();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Eroare la salvare';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDelete = (device: DeviceListItem) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;
    
    try {
      await deleteDevice(deviceToDelete.id);
      setSnackbar({ open: true, message: 'Aparatul a fost dezactivat', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      loadDevices();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Eroare la dezactivare';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  // Get institution options for autocomplete
  const institutionOptions = useMemo(() => {
    return institutions.map(i => ({ id: i.id, label: i.name }));
  }, [institutions]);

  return (
    <Box>
      {/* Filters - Compact */}
      <Card sx={{ mb: 1.5, bgcolor: 'background.paper' }}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Caută după cod, nume..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                sx={compactInputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1rem' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" sx={compactInputSx}>
                <InputLabel>Tip aparat</InputLabel>
                <Select
                  value={filters.deviceTypeId || ''}
                  onChange={(e) => handleFilterChange('deviceTypeId', e.target.value ? Number(e.target.value) : undefined)}
                  label="Tip aparat"
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                  {deviceTypes.map((dt) => (
                    <MenuItem key={dt.id} value={dt.id} sx={{ fontSize: '0.8rem' }}>{dt.code} - {dt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" sx={compactInputSx}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  label="Status"
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                  {Object.entries(DEVICE_STATUS_CONFIG).map(([key, config]) => (
                    <MenuItem key={key} value={key} sx={{ fontSize: '0.8rem' }}>{config.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                >
                  Caută
                </Button>
                <IconButton onClick={loadDevices} size="small" title="Reîncarcă">
                  <RefreshIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreate}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                >
                  Adaugă Aparat
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table - Compact */}
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, p: 1.5, pb: 0.5 }}>
            Lista Aparate ({total})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeaderSx}>
                <TableCell>Cod</TableCell>
                <TableCell>Nume</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Instituție</TableCell>
                <TableCell>Sală</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      Nu există aparate
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((device) => (
                  <TableRow key={device.id} hover sx={{ '& td': tableCellSx }}>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {device.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{device.name}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getDeviceIcon()}
                        label={device.device_type_code}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>{device.institution_name}</TableCell>
                    <TableCell>{device.room}</TableCell>
                    <TableCell>
                      <Chip
                        label={DEVICE_STATUS_CONFIG[device.status]?.label || device.status}
                        color={DEVICE_STATUS_CONFIG[device.status]?.color || 'default'}
                        size="small"
                        sx={{ height: 18, fontSize: '0.68rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Program">
                        <IconButton size="small" onClick={() => {/* TODO: Schedule dialog */}}>
                          <ScheduleIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editează">
                        <IconButton size="small" onClick={() => handleOpenEdit(device)}>
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dezactivează">
                        <IconButton size="small" onClick={() => handleOpenDelete(device)} color="error">
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={Math.floor((filters.skip || 0) / (filters.limit || 25))}
            onPageChange={handleChangePage}
            rowsPerPage={filters.limit || 25}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Pe pagină:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} din ${count}`}
            sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog - Compact */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={dialogTitleSx}>
          {editingDevice ? 'Editează Aparat' : 'Adaugă Aparat Nou'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {formLoading && !editingDevice ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={institutionOptions}
                  value={institutionOptions.find(i => i.id === formData.tenant_id) || null}
                  onChange={(_, newValue) => setFormData(prev => ({ ...prev, tenant_id: newValue?.id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Instituție *" size="small" sx={compactInputSx} />
                  )}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" required sx={compactInputSx}>
                  <InputLabel>Tip Aparat</InputLabel>
                  <Select
                    value={formData.device_type_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, device_type_id: Number(e.target.value) }))}
                    label="Tip Aparat"
                  >
                    {deviceTypes.map((dt) => (
                      <MenuItem key={dt.id} value={dt.id} sx={{ fontSize: '0.8rem' }}>{dt.code} - {dt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cod *"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="CT-1, MR-A"
                  inputProps={{ maxLength: 50 }}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Denumire *"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Brand"
                  value={formData.brand || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="Siemens, GE, Philips"
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Model"
                  value={formData.model || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Număr Serial"
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sală / Cameră *"
                  value={formData.room}
                  onChange={(e) => setFormData(prev => ({ ...prev, room: e.target.value }))}
                  placeholder="Sala CT, Camera 101"
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="DICOM AE Title"
                  value={formData.dicom_ae_title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, dicom_ae_title: e.target.value.toUpperCase() }))}
                  placeholder="CT_SCANNER_1"
                  sx={compactInputSx}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notițe"
                  multiline
                  rows={2}
                  value={formData.notes || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  sx={compactInputSx}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Anulează
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={formLoading}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {formLoading ? <CircularProgress size={16} /> : (editingDevice ? 'Salvează' : 'Adaugă')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Compact */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={dialogTitleSx}>Confirmă Dezactivarea</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>
            Sigur doriți să dezactivați aparatul <strong>{deviceToDelete?.code} - {deviceToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Anulează
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmDelete}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Dezactivează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ fontSize: '0.78rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DevicesPage;
